// Lightweight, dependency-free SVG charts in the Beacon palette.
import { moneyShort } from "../lib/format";

const W = 680, PADL = 56, PADR = 16, PADT = 14, PADB = 28;

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / p;
  const m = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return m * p;
}
function ticks(n: number): number[] {
  if (n <= 8) return Array.from({ length: n }, (_, i) => i);
  const step = Math.ceil(n / 6);
  const out: number[] = [];
  for (let i = 0; i < n; i += step) out.push(i);
  if (out[out.length - 1] !== n - 1) out.push(n - 1);
  return out;
}

export interface Series { name: string; color: string; values: number[]; fill?: boolean; dashed?: boolean; }

function Legend({ items }: { items: { name: string; color: string }[] }) {
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center", marginTop: 6 }}>
      {items.map((it) => (
        <span key={it.name} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--muted)" }}>
          <span style={{ width: 11, height: 11, borderRadius: 3, background: it.color, display: "inline-block" }} />
          {it.name}
        </span>
      ))}
    </div>
  );
}

/** Multi-line trend chart (e.g. Value vs Loan vs Equity over the years). */
export function LineChart({ years, series, height = 236 }: { years: number[]; series: Series[]; height?: number }) {
  const H = height, plotW = W - PADL - PADR, plotH = H - PADT - PADB;
  const n = years.length;
  const maxV = niceMax(Math.max(1, ...series.flatMap((s) => s.values)));
  const x = (i: number) => PADL + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v: number) => PADT + plotH - (v / maxV) * plotH;
  const grid = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" style={{ display: "block" }}>
        {grid.map((f) => {
          const gy = PADT + plotH - f * plotH;
          return (
            <g key={f}>
              <line x1={PADL} y1={gy} x2={W - PADR} y2={gy} stroke="var(--line)" strokeWidth={1} />
              <text x={PADL - 8} y={gy + 3.5} textAnchor="end" fontSize={10} fill="var(--muted)">{moneyShort(f * maxV)}</text>
            </g>
          );
        })}
        {series.map((s) => {
          const pts = s.values.map((v, i) => `${x(i)},${y(v)}`).join(" ");
          const area = `M ${x(0)},${PADT + plotH} L ${s.values.map((v, i) => `${x(i)},${y(v)}`).join(" L ")} L ${x(n - 1)},${PADT + plotH} Z`;
          return (
            <g key={s.name}>
              {s.fill && <path d={area} fill={s.color} opacity={0.12} />}
              <polyline points={pts} fill="none" stroke={s.color} strokeWidth={2.2} strokeLinejoin="round" strokeLinecap="round" strokeDasharray={s.dashed ? "5 4" : undefined} />
            </g>
          );
        })}
        {ticks(n).map((i) => (
          <text key={i} x={x(i)} y={H - 9} textAnchor="middle" fontSize={10} fill="var(--muted)">yr {years[i]}</text>
        ))}
      </svg>
      <Legend items={series.map((s) => ({ name: s.name, color: s.color }))} />
    </div>
  );
}

/** Stacked-area chart (e.g. equity composition: initial / paydown / appreciation / value-add). */
export function StackChart({ years, layers, height = 248 }: { years: number[]; layers: Series[]; height?: number }) {
  const H = height, plotW = W - PADL - PADR, plotH = H - PADT - PADB;
  const n = years.length;
  const totals = years.map((_, i) => layers.reduce((s, l) => s + Math.max(0, l.values[i]), 0));
  const maxV = niceMax(Math.max(1, ...totals));
  const x = (i: number) => PADL + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v: number) => PADT + plotH - (v / maxV) * plotH;
  const grid = [0, 0.25, 0.5, 0.75, 1];

  // cumulative baselines
  const base = years.map(() => 0);
  const bands = layers.map((l) => {
    const lower = base.slice();
    const upper = l.values.map((v, i) => (base[i] += Math.max(0, v)));
    return { color: l.color, name: l.name, lower, upper: upper.slice() };
  });

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" style={{ display: "block" }}>
        {grid.map((f) => {
          const gy = PADT + plotH - f * plotH;
          return (
            <g key={f}>
              <line x1={PADL} y1={gy} x2={W - PADR} y2={gy} stroke="var(--line)" strokeWidth={1} />
              <text x={PADL - 8} y={gy + 3.5} textAnchor="end" fontSize={10} fill="var(--muted)">{moneyShort(f * maxV)}</text>
            </g>
          );
        })}
        {bands.map((b) => {
          const top = b.upper.map((v, i) => `${x(i)},${y(v)}`).join(" L ");
          const bot = b.lower.map((v, i) => `${x(i)},${y(v)}`).reverse().join(" L ");
          return <path key={b.name} d={`M ${top} L ${bot} Z`} fill={b.color} opacity={0.9} stroke="#fff" strokeWidth={0.6} />;
        })}
        {ticks(n).map((i) => (
          <text key={i} x={x(i)} y={H - 9} textAnchor="middle" fontSize={10} fill="var(--muted)">yr {years[i]}</text>
        ))}
      </svg>
      <Legend items={layers.map((l) => ({ name: l.name, color: l.color }))} />
    </div>
  );
}

/** Simple vertical bars (e.g. annual cash flow). */
export function BarChart({ years, values, color = "#2C5C4A", height = 180 }: { years: number[]; values: number[]; color?: string; height?: number }) {
  const H = height, plotW = W - PADL - PADR, plotH = H - PADT - PADB;
  const n = years.length;
  const maxV = niceMax(Math.max(1, ...values));
  const bw = (plotW / n) * 0.6;
  const x = (i: number) => PADL + (i + 0.5) * (plotW / n);
  const y = (v: number) => PADT + plotH - (v / maxV) * plotH;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" style={{ display: "block" }}>
      {[0, 0.5, 1].map((f) => {
        const gy = PADT + plotH - f * plotH;
        return <g key={f}><line x1={PADL} y1={gy} x2={W - PADR} y2={gy} stroke="var(--line)" /><text x={PADL - 8} y={gy + 3.5} textAnchor="end" fontSize={10} fill="var(--muted)">{moneyShort(f * maxV)}</text></g>;
      })}
      {values.map((v, i) => (
        <rect key={i} x={x(i) - bw / 2} y={y(Math.max(0, v))} width={bw} height={Math.abs(PADT + plotH - y(Math.max(0, v)))} rx={2} fill={color} opacity={0.85} />
      ))}
      {ticks(n).map((i) => (
        <text key={i} x={x(i)} y={H - 9} textAnchor="middle" fontSize={10} fill="var(--muted)">yr {years[i]}</text>
      ))}
    </svg>
  );
}
